import type { World, WorldCell, WorldRegion, WorldTerrain } from '@mezha/api'

export type Box = { x0: number; y0: number; cols: number; rows: number }

/** The comfortable cell on a computer: the map shows as many cells of this size as the screen holds. */
export const CELL_COMFORT = 64
/** A smaller cell is used only to show the whole world at once. */
export const CELL_FIT = 56
/** Largest cell: a small world on a big screen stops growing here. */
export const CELL_MAX = 112
/** The cell on a phone. */
export const CELL_PHONE = 60
/** The cell size the map art is drawn for; marks inside a cell scale by cs / CELL_BASE. */
export const CELL_BASE = 80

/** The rectangle of world cells covered by the regions. */
export function worldBounds(regions: WorldRegion[]): Box {
  const x0 = Math.min(...regions.map(r => r.origin_x))
  const y0 = Math.min(...regions.map(r => r.origin_y))
  const x1 = Math.max(...regions.map(r => r.origin_x + r.width))
  const y1 = Math.max(...regions.map(r => r.origin_y + r.height))
  return { x0, y0, cols: x1 - x0, rows: y1 - y0 }
}

/** The part of the world to draw. The whole world when it fits with cells of
 *  at least CELL_FIT px (they then grow up to CELL_MAX); otherwise as many
 *  comfortable cells as the screen holds, stretched to fill it, in a window
 *  around the hero clamped to the edges of the world. */
export function visibleWindow(world: Box, hero: { x: number; y: number }, avail: { w: number; h: number }, phone = false, focus?: Box): Box & { cs: number } {
  const fit = (c: number, r: number) => Math.floor(Math.min(avail.w / c, avail.h / r))
  if (!phone && (!focus || world.rows <= focus.rows) && fit(world.cols, world.rows) >= CELL_FIT) {
    return { ...world, cs: Math.min(CELL_MAX, fit(world.cols, world.rows)) }
  }
  const want = phone ? CELL_PHONE : CELL_COMFORT
  // Shorter regional maps should not inherit empty north/south strips from a
  // taller neighbour. Reserve up to six extra columns for surrounding lands.
  const frame = focus ?? world
  const cols = Math.max(3, Math.min(world.cols, focus ? focus.cols + 6 : world.cols, Math.floor(avail.w / want)))
  const rows = Math.max(3, Math.min(frame.rows, Math.floor(avail.h / want)))
  const cs = Math.max(44, Math.min(phone ? 72 : CELL_MAX, fit(cols, rows)))
  const x0 = world.x0 + Math.min(Math.max(0, hero.x - world.x0 - Math.floor(cols / 2)), Math.max(0, world.cols - cols))
  const y0 = frame.y0 + Math.min(Math.max(0, hero.y - frame.y0 - Math.floor(rows / 2)), Math.max(0, frame.rows - rows))
  return { x0, y0, cols, rows, cs }
}

export type Dir = 'n' | 's' | 'w' | 'e'
export const DIRS: Record<Dir, { dx: number; dy: number; label: string; arrow: string }> = {
  n: { dx: 0, dy: -1, label: 'Север', arrow: '▴' },
  w: { dx: -1, dy: 0, label: 'Запад', arrow: '◂' },
  e: { dx: 1, dy: 0, label: 'Восток', arrow: '▸' },
  s: { dx: 0, dy: 1, label: 'Юг', arrow: '▾' },
}

const OPPOSITE: Record<Dir, Dir> = { n: 's', s: 'n', e: 'w', w: 'e' }

/** Direction of an orthogonal neighbour, or null. */
export function dirTo(from: { x: number; y: number }, to: { x: number; y: number }): Dir | null {
  const dx = to.x - from.x
  const dy = to.y - from.y
  if (Math.abs(dx) + Math.abs(dy) !== 1) return null
  return dx === 1 ? 'e' : dx === -1 ? 'w' : dy === 1 ? 's' : 'n'
}

/** Words for where a cell is relative to the hero. */
export function whereText(from: { x: number; y: number }, to: { x: number; y: number }): string {
  if (from.x === to.x && from.y === to.y) return 'вы здесь'
  const d = dirTo(from, to)
  if (d) return { n: 'к северу от вас', s: 'к югу от вас', w: 'к западу от вас', e: 'к востоку от вас' }[d]
  const steps = Math.abs(to.x - from.x) + Math.abs(to.y - from.y)
  return `в ${steps} шагах`
}

/** A side or a corner of a cell. */
export type EdgeSide = Dir | 'ne' | 'nw' | 'se' | 'sw'
/** A neighbour's tile running over an edge of a cell: the picture, which edge,
 *  one of the ragged edge masks, and a narrow edge for a terrain of layer 0
 *  (a road, a village) that keeps its middle clear. */
export type CellEdge = { url: string; side: EdgeSide; variant: number; narrow: boolean }
/** The tile of a cell: its picture, its turn in degrees clockwise and the
 *  neighbours running over its edges, lowest layer first. */
export type CellTile = { url: string; rotate: number; edges: CellEdge[] }

/** Ragged edge masks per side: /assets/map/edge-<side>-<variant>.png. */
export const EDGE_VARIANTS = 3
const CORNERS: Array<{ side: EdgeSide; dx: number; dy: number }> = [
  { side: 'ne', dx: 1, dy: -1 }, { side: 'nw', dx: -1, dy: -1 }, { side: 'se', dx: 1, dy: 1 }, { side: 'sw', dx: -1, dy: 1 },
]

export type RoadShape = 'straight' | 'corner' | 'tee' | 'cross' | 'end'

/** The shape of a road tile and its clockwise turn by the joined sides. The
 *  tiles are drawn as: straight top to bottom, corner top and right, tee top,
 *  right and bottom, end with the exit at the top. A road joined nowhere is
 *  drawn straight. */
export function roadShape(j: Record<Dir, boolean>): { shape: RoadShape; rotate: number } {
  const sides = (['n', 'e', 's', 'w'] as const).filter(d => j[d])
  const turn = (d: Dir) => ({ n: 0, e: 90, s: 180, w: 270 })[d]
  switch (sides.length) {
    case 4:
      return { shape: 'cross', rotate: 0 }
    case 3: {
      const missing = (['n', 'e', 's', 'w'] as const).find(d => !j[d])!
      // The tee misses the left side at 0°; each quarter turn moves the gap one side on.
      return { shape: 'tee', rotate: (turn(missing) + 90) % 360 }
    }
    case 2:
      if (j.n && j.s) return { shape: 'straight', rotate: 0 }
      if (j.e && j.w) return { shape: 'straight', rotate: 90 }
      // The corner joins a side and the next one clockwise; it turns to the first of them.
      return { shape: 'corner', rotate: turn(j.w && j.n ? 'w' : sides[0]) }
    case 1:
      return { shape: 'end', rotate: turn(sides[0]) }
    default:
      return { shape: 'straight', rotate: 0 }
  }
}

/** A cell of the world map with the region it belongs to. */
export type MapCell = WorldCell & { region: WorldRegion }

/** Every cell the hero sees — his region and the regions nearby — by "x,y". */
export class WorldMap {
  readonly regions: WorldRegion[]
  readonly bounds: Box
  private readonly cells = new Map<string, MapCell>()

  constructor(w: Pick<World, 'region' | 'nearby'>) {
    this.regions = [w.region, ...w.nearby]
    this.bounds = worldBounds(this.regions)
    for (const r of this.regions) for (const c of r.cells) this.cells.set(`${c.x},${c.y}`, { ...c, region: r })
  }

  at(x: number, y: number): MapCell | undefined {
    return this.cells.get(`${x},${y}`)
  }

  /** The terrain definition of a cell (terrains are per region). */
  terrain(c: MapCell) {
    return c.region.terrains.find(t => t.id === c.terrain)
  }

  /** The plain tile of a cell: a terrain with two tiles alternates them in a
   *  fixed pattern, so the same field never looks like wallpaper. */
  private plainTile(c: MapCell, t: WorldTerrain): string {
    return t.tile_alt && (c.x * 7 + c.y * 13) % 3 === 0 ? t.tile_alt : t.tile
  }

  /** The map tile of a cell and its turn in degrees clockwise. A terrain
   *  with tiles by shape (a road) joins the neighbours whose terrain it links
   *  to: the shape and turn come from which sides are joined; a road ending
   *  at the edge of the map goes on beyond it. The neighbours of a higher
   *  layer run over the edges of the cell, so terrains blend instead of
   *  meeting as squares. */
  tile(c: MapCell): CellTile {
    const t = this.terrain(c)
    if (!t) return { url: '', rotate: 0, edges: [] }
    const edges = this.edges(c, t)
    if (t.shapes) {
      const near = (d: Dir) => this.at(c.x + DIRS[d].dx, c.y + DIRS[d].dy)
      const j = {} as Record<Dir, boolean>
      for (const d of ['n', 'e', 's', 'w'] as const) {
        const n = near(d)
        j[d] = Boolean(n && t.links.includes(n.terrain))
      }
      // A road that comes to the edge of the map goes on beyond it rather than ending there.
      const one = (['n', 'e', 's', 'w'] as const).filter(d => j[d])
      if (one.length === 1 && !near(OPPOSITE[one[0]])) j[OPPOSITE[one[0]]] = true
      const { shape, rotate } = roadShape(j)
      return { url: t.shapes[shape], rotate, edges }
    }
    return { url: this.plainTile(c, t), rotate: 0, edges }
  }

  /** The neighbours running over the edges of c, lowest layer first: a
   *  terrain of a higher layer, and the first tile of the same terrain over
   *  its second. A corner is covered only by a diagonal neighbour whose
   *  terrain is on neither side next to it (a side covers the corner). */
  private edges(c: MapCell, t: WorldTerrain): CellEdge[] {
    const variant = (c.x * 5 + c.y * 11) % EDGE_VARIANTS
    const narrow = t.blend === 0
    const own = t.shapes ? '' : this.plainTile(c, t)
    const runs = (n: MapCell | undefined): { layer: number; url: string; id: string } | null => {
      const nt = n && this.terrain(n)
      if (!n || !nt || !nt.blend || nt.shapes) return null
      const url = this.plainTile(n, nt)
      if (!url) return null
      if (nt.id !== t.id) return nt.blend > t.blend ? { layer: nt.blend, url, id: nt.id } : null
      return url === nt.tile && own !== url ? { layer: t.blend, url, id: nt.id } : null
    }
    const out: Array<CellEdge & { layer: number }> = []
    for (const d of ['n', 'e', 's', 'w'] as const) {
      const r = runs(this.at(c.x + DIRS[d].dx, c.y + DIRS[d].dy))
      if (r) out.push({ url: r.url, side: d, variant, narrow, layer: r.layer })
    }
    for (const k of CORNERS) {
      const r = runs(this.at(c.x + k.dx, c.y + k.dy))
      const beside = [this.at(c.x + k.dx, c.y), this.at(c.x, c.y + k.dy)]
      if (r && !beside.some(b => b?.terrain === r.id)) out.push({ url: r.url, side: k.side, variant, narrow, layer: r.layer })
    }
    return out.sort((a, b) => a.layer - b.layer).map(({ layer: _layer, ...e }) => e)
  }

  /** True when a step from the hero's region into c crosses a border (and pays its toll). */
  across(home: WorldRegion, c: MapCell): boolean {
    return c.region.id !== home.id
  }
}

/** A step is possible to a passable, open, orthogonal neighbour. */
export const canStep = (from: { x: number; y: number }, c: WorldCell | undefined): boolean =>
  Boolean(c && c.passable && !c.locked && dirTo(from, c))
